/** Shared primitives. Every visual decision here comes from the design system. */

import { useState } from 'react'
import type { ReactNode } from 'react'
import { X } from 'lucide-react'

import { usePermission } from '@/lib/session'

type Tone = 'neutral' | 'green' | 'amber' | 'red' | 'info' | 'orange'

const TONE_VAR: Record<Tone, string> = {
  neutral: 'var(--status-neutral)',
  green: 'var(--status-green)',
  amber: 'var(--status-amber)',
  red: 'var(--status-red)',
  info: 'var(--status-info)',
  orange: 'var(--status-orange)',
}

/**
 * Hides functionality the user cannot use.
 *
 * Usability only. The API enforces the same permission independently, so removing this
 * component would change what the interface looks like and nothing about what anyone
 * can actually do.
 */
export function Can({
  permission,
  children,
  fallback = null,
}: {
  permission: string | string[]
  children: ReactNode
  fallback?: ReactNode
}) {
  const permissions = Array.isArray(permission) ? permission : [permission]
  return usePermission(...permissions) ? <>{children}</> : <>{fallback}</>
}

/** Status chip: tinted background, full-strength text. */
export function Pill({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  const color = TONE_VAR[tone]
  return (
    <span
      className="micro-label inline-flex items-center rounded-pill px-2 py-0.5"
      style={{ color, backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)` }}
    >
      {children}
    </span>
  )
}

export function SectionHeader({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-3 flex items-center justify-between">
      <h2 className="section-rule text-fg">{children}</h2>
      {action}
    </div>
  )
}

/**
 * A headline number.
 *
 * Pass `onClick` and it becomes a real button: it lifts on hover, and pressing it filters
 * the list below to the projects behind the number. A figure you can't interrogate invites
 * the question "which four?" and then leaves you counting rows by hand.
 *
 * `active` marks the one currently filtering, so the number and the list can never
 * disagree about what is being shown.
 */
export function StatCard({
  label,
  value,
  tone = 'info',
  onClick,
  active = false,
  hint,
}: {
  label: string
  value: ReactNode
  tone?: Tone
  onClick?: () => void
  active?: boolean
  hint?: string
}) {
  const body = (
    <>
      <div className="font-heading text-[34px] leading-none text-fg">{value}</div>
      <div className="micro-label mt-1 text-fg-muted">{label}</div>
    </>
  )

  const style = {
    borderLeft: `3px solid ${TONE_VAR[tone]}`,
    ...(active ? { backgroundColor: `color-mix(in srgb, ${TONE_VAR[tone]} 10%, var(--surface))` } : {}),
  }

  if (!onClick) {
    return (
      <div className="card flex-1 px-4 py-3" style={style}>
        {body}
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={hint ?? `Show ${label.toLowerCase()}`}
      // The lift: a small rise and a shadow on hover, and it presses back in on click, so
      // the card reads as something you can push before you try.
      className={[
        'card flex-1 px-4 py-3 text-left transition-all duration-150',
        'hover:-translate-y-1 hover:shadow-lg active:translate-y-0 active:shadow-sm',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
      ].join(' ')}
      style={{ ...style, outlineColor: TONE_VAR[tone] }}
    >
      {body}
    </button>
  )
}

export function Button({
  variant = 'secondary',
  type = 'button',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'destructive'
}) {
  const base =
    'inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50'
  const variants = {
    // Near-black on brand yellow, never white.
    primary: 'bg-brand text-brand-on hover:brightness-95',
    secondary: 'border border-border text-fg hover:bg-canvas',
    destructive: 'text-white hover:brightness-95',
  }
  return (
    <button
      type={type}
      className={`${base} ${variants[variant]} ${props.className ?? ''}`}
      style={variant === 'destructive' ? { backgroundColor: 'var(--status-red)' } : undefined}
      {...props}
    />
  )
}

export function Field({
  label,
  hint,
  children,
}: {
  // ReactNode so a label can carry an info affordance beside its text. Plain strings, which
  // is nearly every use, are unaffected.
  label: ReactNode
  hint?: string
  children: ReactNode
}) {
  return (
    <label className="block">
      <span className="micro-label text-fg-muted">{label}</span>
      <div className="mt-1">{children}</div>
      {hint ? <p className="mt-1 text-xs text-fg-muted">{hint}</p> : null}
    </label>
  )
}

export const inputClass =
  'w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-fg placeholder:text-fg-muted'

/**
 * A date box with a one-click clear.
 *
 * The native date input has no way to empty it short of deleting the day, month and year
 * one segment at a time — five actions to say "no date". Emits the ISO string, or '' when
 * cleared, so callers keep treating blank as null exactly as before.
 */
export function DateInput({
  value,
  onChange,
  className = '',
  'aria-label': ariaLabel,
}: {
  value: string
  onChange: (value: string) => void
  className?: string
  'aria-label'?: string
}) {
  return (
    <span className={`relative block ${className}`}>
      <input
        type="date"
        className={`${inputClass} ${value ? 'pr-8' : ''}`}
        value={value}
        aria-label={ariaLabel}
        onChange={(event) => onChange(event.target.value)}
      />
      {value ? (
        <button
          type="button"
          aria-label={ariaLabel ? `Clear ${ariaLabel.toLowerCase()}` : 'Clear date'}
          title="Clear date"
          className="absolute right-1.5 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-fg-muted hover:bg-canvas hover:text-fg"
          onClick={(event) => {
            // Inside a <label> a click would otherwise be forwarded to the input and open
            // the browser's date picker over the value just cleared.
            event.preventDefault()
            onChange('')
          }}
        >
          <X size={13} aria-hidden />
        </button>
      ) : null}
    </span>
  )
}

/** Loading, empty and error states are designed, not an afterthought. */
export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 py-10 text-sm text-fg-muted" role="status">
      <span className="h-3 w-3 animate-pulse rounded-full bg-brand" />
      {label}…
    </div>
  )
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="card px-6 py-10 text-center">
      <p className="font-heading text-lg text-fg">{title}</p>
      {children ? <div className="mt-1 text-sm text-fg-muted">{children}</div> : null}
    </div>
  )
}

/**
 * Error state.
 *
 * Shows the correlation id when the API supplied one. That id is the whole reason a
 * support conversation can start with "here is exactly what happened" instead of "it
 * broke at some point this afternoon".
 */
export function ErrorState({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : 'Something went wrong.'
  const correlationId =
    typeof error === 'object' && error !== null && 'correlationId' in error
      ? (error as { correlationId: string | null }).correlationId
      : null

  return (
    <div className="card px-6 py-6" role="alert" style={{ borderLeft: '3px solid var(--status-red)' }}>
      <p className="font-semibold text-fg">{message}</p>
      {correlationId ? (
        <p className="mt-2 text-xs text-fg-muted">
          Reference <code className="font-mono">{correlationId}</code> when reporting this.
        </p>
      ) : null}
    </div>
  )
}

/** A small "i" that reveals an explanation in place.
 *
 * For the case where a label is not self-explanatory and a permanent paragraph would be
 * clutter — a phase gate, a scoring scale. Click rather than hover, so it works on a touch
 * screen and for anyone driving by keyboard.
 */
export function InfoDot({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <span className="relative inline-block align-middle">
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-border text-[10px] font-semibold text-fg-muted hover:text-fg"
        onClick={() => setOpen((value) => !value)}
      >
        i
      </button>
      {open ? (
        <span className="card absolute left-0 top-6 z-20 block w-[min(22rem,70vw)] p-3 text-xs font-normal leading-relaxed text-fg-muted shadow-lg">
          {children}
        </span>
      ) : null}
    </span>
  )
}
